import axios, { isAxiosError } from "axios";
import { aiClient, rmsClient } from "@/shared/api/clients";

/** Raw /templates response — callers own the array/object-shape normalization. */
export async function fetchTemplatesRaw(
  stationId: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await rmsClient.get("/templates", { params: { stationId }, signal });
  return response.data;
}

export async function deleteTemplate(input: {
  templateId: string;
  stationId: string | null;
}): Promise<void> {
  await rmsClient.delete("/template", { data: input });
}

/** Covers both rename and sign-off — pass the exact payload the server expects. */
export async function saveTemplate(body: {
  template: Record<string, unknown>;
  examples: string[];
}): Promise<void> {
  await rmsClient.put("/template", body);
}

/*
 * Narrative Template API (agent API, WorkOS request authorizer).
 * Flow: upload-template → PUT each presignedUrl → process-template → poll
 * get-template-job → optional edit-template.
 */

/** Every narrative-template endpoint fails with this shape. */
export interface TemplateApiErrorBody {
  error: string;
  details?: string;
}

/**
 * Unwraps `{ error, details? }` into one displayable string. `details` carries
 * the actionable half on validation failures (e.g. "Invalid file metadata" →
 * "file at index 2: missing or invalid contentType"), so it must not be dropped.
 */
export function templateErrorMessage(err: unknown, fallback: string): string {
  if (isAxiosError<TemplateApiErrorBody>(err)) {
    const body = err.response?.data;
    if (body?.error) {
      return body.details ? `${body.error}: ${body.details}` : body.error;
    }
  }
  return fallback;
}

/** `pending_upload` is the initial state; it precedes `processing`. */
export type TemplateJobStatus =
  | "pending_upload"
  | "processing"
  | "completed"
  | "failed";

export interface TemplateResult {
  /** Handlebars text: flat snake_case vars, every one wrapped in {{#if}}, no {{#each}}. */
  template: string;
  /** Exactly 3 anonymized narratives; PII is redacted to the literal token `***`. */
  generatedExamples: string[];
}

export interface TemplateUploadInitResponse {
  jobId: string;
  uploads: {
    /** Echoes the submitted fileName — the only safe key to match uploads back to files. */
    fileName: string;
    /** S3 PUT URL, valid 1 hour. */
    presignedUrl: string;
    s3Key: string;
  }[];
}

/**
 * Creates the job and returns one presigned S3 PUT URL per file. Uploads
 * nothing itself. Server-side caps: 25 files, extensions .pdf/.docx/.doc/.txt/.rtf.
 * Keep the UI cap at MAX_FILES (10) — see processTemplate.
 */
export async function initTemplateUpload(
  files: { fileName: string; fileSize: number; contentType: string }[],
): Promise<TemplateUploadInitResponse> {
  const { data } = await aiClient.post("/upload-template", { files });
  return data;
}

/** 202 — the async generate invoke has been made; poll from here. */
export interface ProcessTemplateResponse {
  jobId: string;
  status: "processing";
}

/**
 * Kicks off extraction + generation in the background and returns 202
 * immediately. The downstream generate step hard-rejects more than 10 example
 * reports, and the job ends `failed` — hence the client-side cap of 10 files.
 */
export async function processTemplate(jobId: string): Promise<ProcessTemplateResponse> {
  const { data } = await aiClient.post("/process-template", { jobId });
  return data;
}

export interface EditTemplateResponse {
  template: string;
  generatedExamples: string[];
  /** 1–2 sentence description of the change; render as the assistant turn. */
  summary: string;
}

/**
 * Conversational refinement of a completed job. Edit history lives server-side
 * (last 10 turns replayed), so send only the new message. The new result is
 * written back to the job row, so no reprocessing is needed after an edit.
 * Synchronous model call — multi-second latency, 120s server timeout.
 */
export async function editTemplate(input: {
  jobId: string;
  message: string;
}): Promise<EditTemplateResponse> {
  const { data } = await aiClient.post("/edit-template", input);
  return data;
}

export interface TemplateJobResponse {
  jobId: string;
  status: TemplateJobStatus;
  /** Present when status === "completed". */
  result?: TemplateResult;
  /** Present when status === "failed". */
  error?: string;
}

export async function getTemplateJob(
  jobId: string,
  signal?: AbortSignal,
): Promise<TemplateJobResponse> {
  const { data } = await aiClient.get("/get-template-job", { params: { jobId }, signal });
  return data;
}

/**
 * Deliberately unauthenticated relative GET — this endpoint has never been
 * sent a token and must not start receiving one. Returns the raw response so
 * callers can read content-disposition.
 */
export async function downloadTemplateBlob(templateId: string) {
  return axios.get(`/api/templates/downloadTemplate/${encodeURIComponent(templateId)}`, {
    responseType: "blob",
  });
}
